import salesApi from '@/api/sales.api';

const state = {
  quotes: [],
  quote: null,
  status: '',
  query: '',
};

const getters = {
  quotes: state => state.quotes,
  quote: state => state.quote,
  status: state => state.status,
  query: state => state.query,
};

const actions = {
  async findQuotes({ commit, state }, { query }) {
    try {
      commit('SET_QUERY', query);
      commit('SET_STATUS', 'LOADING');
      const response = await salesApi.findQuotes({ query });
      if (state.query !== query) return;
      commit('SET_QUOTES', response.quotes);
      commit('SET_STATUS', 'FINISHED');
    } catch (error) {
      commit('SET_STATUS', 'ERROR');
      console.error('Error fetching quotes:', error);
      throw error;
    }
  },

  async getQuote({ commit }, quoteId) {
    try {
      commit('SET_STATUS', 'LOADING');
      const response = await salesApi.getQuote(quoteId);
      commit('SET_QUOTE', response.quote);
      commit('SET_STATUS', 'FINISHED');
    } catch (error) {
      commit('SET_STATUS', 'ERROR');
      console.error('Error fetching quote:', error);
      throw error;
    }
  },

  async createQuote({ dispatch, state }, quoteData) {
    try {
      const response = await salesApi.createQuote(quoteData);
      dispatch('findQuotes', { query: state.query });
      return response.quote;
    } catch (error) {
      console.error('Error creating quote:', error);
      throw error;
    }
  },

  async updateQuote({ dispatch, state }, { quoteId, ...quoteData }) {
    try {
      const response = await salesApi.updateQuote(quoteId, quoteData);
      dispatch('findQuotes', { query: state.query });
      return response.quote;
    } catch (error) {
      console.error('Error updating quote:', error);
      throw error;
    }
  },

  async copyQuote({ dispatch, state }, { quoteId, options }) {
    try {
      const response = await salesApi.copyQuote(quoteId, options);
      await dispatch('findQuotes', { query: state.query });
      return response.quote;
    } catch (error) {
      console.error('Error copying quote:', error);
      throw error;
    }
  },

  async deleteQuote({ dispatch, state }, quoteId) {
    try {
      await salesApi.deleteQuote(quoteId);
      dispatch('findQuotes', { query: state.query });
    } catch (error) {
      console.error('Error deleting quote:', error);
      throw error;
    }
  },

  async calculateQuote({ commit }, quoteData) {
    try {
      const response = await salesApi.calculateQuote(quoteData);
      return response.quote;
    } catch (error) {
      console.error('Error calculating quote:', error);
      throw error;
    }
  },

  async downloadQuotePDF({ commit }, quoteId) {
    try {
      const response = await salesApi.downloadQuotePDF(quoteId);
      return response;
    } catch (error) {
      console.error('Error downloading quote PDF:', error);
      throw error;
    }
  },

  async getQuotePDFPreview({ commit }, quoteId) {
    try {
      const response = await salesApi.getQuotePDFPreview(quoteId);
      return response;
    } catch (error) {
      console.error('Error getting quote PDF preview:', error);
      throw error;
    }
  },

  async getForecast({ commit }, year) {
    try {
      return await salesApi.getForecast(year);
    } catch (error) {
      console.error('Error fetching sales forecast:', error);
      throw error;
    }
  },

  async getProjectTimeline({ commit }, year) {
    try {
      return await salesApi.getProjectTimeline(year);
    } catch (error) {
      console.error('Error fetching project timeline:', error);
      throw error;
    }
  },

  async getWonRejected({ commit }, year) {
    try {
      return await salesApi.getWonRejected(year);
    } catch (error) {
      console.error('Error fetching won/rejected quotes:', error);
      throw error;
    }
  },

  async getBudget({ commit }, year) {
    try {
      return await salesApi.getBudget(year);
    } catch (error) {
      console.error('Error fetching sales budget:', error);
      throw error;
    }
  },

  async updateBudget({ commit }, { year, months }) {
    try {
      return await salesApi.updateBudget(year, months);
    } catch (error) {
      console.error('Error updating sales budget:', error);
      throw error;
    }
  },
};

const mutations = {
  SET_QUOTES(state, quotes) {
    state.quotes = quotes;
  },
  SET_QUOTE(state, quote) {
    state.quote = quote;
  },
  SET_STATUS(state, status) {
    state.status = status;
  },
  SET_QUERY(state, query) {
    state.query = query;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}; 